import { useCallback, useRef, useState, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import {
  Alert,
  Button,
  DebouncedSearchInput,
  Empty,
  Select,
  Skeleton,
  TablePagination,
} from '@/ui';

import { TopbarActions } from '../../components/Topbar';
import { resolveApiErrorMessage } from '../../lib/apiError';
import { toNumber } from '../../lib/format';
import {
  ALL_SHIPMENT_STATUSES,
  SHIPMENT_STATUSES,
} from '../../lib/shipmentStatus';
import { formatSort } from '../../lib/tableSort';
import { ShipmentEditorDialog } from '../shipment-actions';
import { useShipmentsInWork } from './api/queries';
import {
  DEFAULT_SORT_VALUE,
  isShipmentOrigin,
  PAGE_SIZE,
  resolveShipmentsSearch,
  SORT_OPTIONS,
  type ShipmentsSearch,
  type StatusFilter,
} from './lib/searchParams';
import { ShipmentCard } from './ShipmentCard';

const ROUTE_ID = '/_app/shipments' as const;
const ROUTE_PATH = '/shipments' as const;

/** Сколько заглушек показать, пока первая страница едет. */
const SKELETON_CARDS = 4;

/*
 * Отгрузки в работе - ShipmentList макета (v4): карточки по одной в строку под
 * панелью фильтров. По умолчанию активные (всё, кроме завершённых, возвратов и
 * отмен), можно «Все статусы» или один статус; поиск по клиенту, товару или
 * номеру, «Только задержанные». Плюс происхождение (созданные у нас или
 * унаследованные от заказов 1С) и «С расхождениями с 1С» (ADR-012). Страницы
 * режет сервер; фильтры, сортировка и страница - в адресной строке.
 */
export const ShipmentsPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolveShipmentsSearch(search);
  const [isCreating, setIsCreating] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  /** Новый фильтр - с первой страницы. */
  const setSearch = useCallback(
    (patch: Partial<ShipmentsSearch>) => {
      void navigate({
        from: ROUTE_PATH,
        to: ROUTE_PATH,
        search: (previous: ShipmentsSearch) => ({
          ...previous,
          ...patch,
          page: undefined,
        }),
      });
    },
    [navigate],
  );

  /*
   * Карточки длинные: новая страница начинается сверху, а не там, где нажали.
   * Прокрутку ведёт экран, а не роутер: его восстановление вернуло бы область
   * прокрутки туда, где был клик.
   */
  const goToPage = (page: number) => {
    void navigate({
      from: ROUTE_PATH,
      to: ROUTE_PATH,
      search: (previous: ShipmentsSearch) => ({
        ...previous,
        page: page === 1 ? undefined : page,
      }),
      resetScroll: false,
    });
    filtersRef.current?.scrollIntoView({ block: 'start' });
  };

  const { data, isPending, isError, error } = useShipmentsInWork(resolved);

  const shipments = data?.items ?? [];
  const total = toNumber(data?.totalRecords ?? 0);
  const totalPages = Math.max(1, toNumber(data?.totalPages ?? 1));
  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);
  const sortValue = formatSort(resolved.sort);

  const isUnfiltered =
    resolved.search === '' &&
    resolved.status === 'active' &&
    resolved.origin === 'all' &&
    !resolved.discrepancies &&
    !resolved.delayed;

  return (
    <>
      <TopbarActions>
        <Button variant="primary" onClick={() => setIsCreating(true)}>
          <Plus aria-hidden /> Новая отгрузка
        </Button>
      </TopbarActions>

      <div
        ref={filtersRef}
        className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3"
      >
        <DebouncedSearchInput
          className="min-w-[220px] flex-1"
          placeholder="Поиск по клиенту, товару, №..."
          aria-label="Поиск по клиенту, товару или номеру"
          value={resolved.search}
          onCommit={(value) => setSearch({ search: value || undefined })}
        />
        <Select
          aria-label="Статус"
          value={resolved.status}
          onChange={(event) => {
            const status = event.target.value as StatusFilter;
            setSearch({ status: status === 'active' ? undefined : status });
          }}
        >
          <option value="active">Активные</option>
          <option value="all">Все статусы</option>
          {ALL_SHIPMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {SHIPMENT_STATUSES[status].label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Происхождение"
          value={resolved.origin}
          onChange={(event) => {
            const origin = event.target.value;
            setSearch({
              origin: isShipmentOrigin(origin) ? origin : undefined,
            });
          }}
        >
          <option value="all">Все отгрузки</option>
          <option value="Culture">Созданные в Culture</option>
          <option value="Onec">По заказам из 1С</option>
        </Select>
        <Select
          aria-label="Сортировка"
          value={sortValue}
          onChange={(event) => {
            const sort = event.target.value;
            setSearch({ sort: sort === DEFAULT_SORT_VALUE ? undefined : sort });
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <label className="inline-flex items-center gap-1.5 px-1.5 text-[13px] text-ink-700">
          <input
            type="checkbox"
            checked={resolved.delayed}
            onChange={(event) =>
              setSearch({ delayed: event.target.checked || undefined })
            }
          />
          Только задержанные
        </label>
        <label className="inline-flex items-center gap-1.5 px-1.5 text-[13px] text-ink-700">
          <input
            type="checkbox"
            checked={resolved.discrepancies}
            onChange={(event) =>
              setSearch({ discrepancies: event.target.checked || undefined })
            }
          />
          С расхождениями с 1С
        </label>
      </div>

      {isError ? (
        <Alert tone="error">
          Не удалось загрузить отгрузки: {resolveApiErrorMessage(error)}.
        </Alert>
      ) : (
        <>
          {isPending && (
            <div className="flex flex-col gap-4">
              {Array.from({ length: SKELETON_CARDS }, (_, index) => (
                <Skeleton key={index} className="h-[150px] rounded-lg" />
              ))}
            </div>
          )}

          {!isPending && shipments.length === 0 && (
            <Empty
              icon={<Search aria-hidden className="size-7" />}
              className="rounded-lg border border-ink-200 bg-white"
            >
              {isUnfiltered
                ? 'Активных отгрузок нет: их создают в плане отгрузок'
                : 'По выбранным фильтрам ничего не найдено'}
            </Empty>
          )}

          {shipments.length > 0 && (
            <div className="flex flex-col gap-4">
              {shipments.map((shipment) => (
                <ShipmentCard key={shipment.id} shipment={shipment} />
              ))}
            </div>
          )}

          {!isPending && total > 0 && (
            <TablePagination
              className="mt-4 rounded-lg border border-ink-200 bg-white px-4 py-2.5 text-xs text-ink-500"
              page={resolved.page}
              totalPages={totalPages}
              onPageChange={goToPage}
              summary={`Показаны ${firstOnPage}-${lastOnPage} из ${total}`}
            />
          )}
        </>
      )}

      {isCreating && (
        <ShipmentEditorDialog onClose={() => setIsCreating(false)} />
      )}
    </>
  );
};
